import React, { useCallback, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { isEmpty } from '../../../helpers/common';
import { LinkModalInterface, ProjectFileData, FileOperationResponse } from '../../../interfaces/ProjectInterface';
import { BUTTON_TYPE, FILE_TYPES } from '../../../global/constants';
import { captureException } from '../../../services/logService';
import {
  addFileData,
  createNewDoc,
  dispatchProjectError,
  dispatchProjectSuccess,
  updateDocumentData,
  updateFileData,
  updateProjectResource
} from '../../../services/projectServices';
import { ModalButton, ModalContent } from './styles';
import Button from '../../button';
import { rootStore } from '../../../mobx/rootStore';
import ModalHeader from '../modalHeader';
import Input from '../../input';

interface FileData {
  onClose: () => void;
  loadFilesData?: () => void;
  modalData?: LinkModalInterface;
  itemData?: ProjectFileData | null;
  folderId?: string | null;
  updateDocName?: (name: string) => void;
  isProjectResource?: boolean;
}

export const FilesModal: React.FC<FileData> = (props) => {
  const { onClose, loadFilesData, itemData, modalData, folderId, updateDocName, isProjectResource = false } = props;
  //States
  const [loading, setLoading] = useState(false);

  //Mobx store variable
  const { projectStore } = rootStore;
  const { currentProject } = projectStore;

  //Other variable
  const fileNameWithoutExtension =
    itemData?.name && itemData?.name?.split('.')?.length > 1
      ? itemData?.name.split('.')?.slice(0, -1).join('.')
      : itemData?.name || '';
  const [docName, setDocName] = useState(fileNameWithoutExtension);
  const history = useHistory();

  const createNewFile = useCallback(async () => {
    let payload: {
      Name: string;
      ProjectId: string;
      UploadFileType: number | undefined;
      ParentId: string | null;
      IsBlocksuite: boolean;
      FileType?: number | null;
      IsSystemFile?: boolean;
      Size?: number | null;
      Url?: string | null;
      IsEdge?: boolean;
    } = {
      Name: docName,
      ProjectId: currentProject?.id || '',
      UploadFileType: modalData?.modalType,
      ParentId: folderId || null,
      IsBlocksuite: modalData?.modalType !== FILE_TYPES.FOLDER
    };
    if (modalData?.modalType === FILE_TYPES.FOLDER) {
      payload.FileType = null;
      payload.IsSystemFile = true;
      payload.Size = null;
      payload.Url = null;
    } else if (modalData?.modalType === FILE_TYPES.WHITEBOARD) {
      payload.IsEdge = true;
    }
    return modalData?.modalType === FILE_TYPES.FOLDER ? await addFileData(payload) : await createNewDoc(payload);
  }, [currentProject?.id, docName, folderId, modalData?.modalType]);

  const handleFolderUpdate = useCallback(async () => {
    let newFolderName;
    if (itemData?.uploadFileType === FILE_TYPES.UPLOAD) {
      const nameParts = itemData?.name?.split('.');
      const extension = nameParts?.length > 1 ? nameParts.pop() : null;
      newFolderName = extension ? `${docName}.${extension}` : docName;
    } else {
      const nameParts = itemData?.name?.split('.');
      const extension = nameParts && nameParts?.length > 1 ? nameParts?.pop() : null;
      newFolderName = extension ? `${docName}.${extension}` : docName;
    }
    let response;
    if (isProjectResource) {
      const payload = {
        label: newFolderName
      };
      response = await updateProjectResource(currentProject?.id, itemData?.id, payload);
      if (response && itemData?.id) {
        currentProject?.updateResource(itemData?.id, { label: newFolderName });
      }
    } else {
      if (itemData?.uploadFileType === FILE_TYPES.DOCS || itemData?.uploadFileType === FILE_TYPES.WHITEBOARD) {
        const payload = { Name: docName };
        response = await updateDocumentData(itemData?.id, payload);
      } else {
        const payload = { Name: newFolderName };
        response = await updateFileData(itemData?.id, payload);
      }
    }
    if (response) {
      const isFolder = itemData?.uploadFileType === FILE_TYPES.FOLDER;
      dispatchProjectSuccess(isFolder ? 'Folder Updated!' : 'File Updated!');
    }
    return response;
  }, [currentProject, docName, isProjectResource, itemData]);

  const handleResponseSuccess = useCallback(
    (response: FileOperationResponse) => {
      if (!response) {
        throw new Error(`Error creating new ${modalData?.name?.toLowerCase()}`);
      }
      onClose();
      loadFilesData?.();
      updateDocName?.(docName);

      // Check if response has documentDetails property (not a boolean)
      if (typeof response !== 'boolean' && response.documentDetails) {
        const docId = response.documentDetails['_id'];
        if (!docId) return;

        if (modalData?.modalType !== FILE_TYPES.FOLDER) {
          const path = `/projects/files/${currentProject?.id}/docs/${docId}?${
            folderId ? `folder=${folderId}&` : ''
          }new=true`;
          history.push(path);
        }
      }
      dispatchProjectSuccess(`${modalData?.name} Added!`);
    },
    [
      currentProject?.id,
      docName,
      folderId,
      history,
      loadFilesData,
      modalData?.modalType,
      modalData?.name,
      onClose,
      updateDocName
    ]
  );

  const createItem = useCallback(
    async (e: React.SyntheticEvent) => {
      e.preventDefault();
      if (isEmpty(docName)) {
        dispatchProjectError(`Please enter a ${modalData?.name?.toLowerCase()} name`);
        return;
      }
      try {
        setLoading(true);
        let response;
        if (!isEmpty(itemData)) {
          response = await handleFolderUpdate();
          if (response) {
            onClose();
            loadFilesData?.();
            updateDocName?.(docName);
          }
        } else {
          response = await createNewFile();
          handleResponseSuccess(response);
        }
      } catch (err) {
        captureException(err);
      } finally {
        setLoading(false);
      }
    },
    [
      docName,
      modalData?.name,
      itemData,
      handleResponseSuccess,
      handleFolderUpdate,
      createNewFile,
      onClose,
      loadFilesData,
      updateDocName
    ]
  );

  return (
    <>
      <ModalHeader
        title={
          isEmpty(itemData?.id)
            ? modalData?.title
            : itemData?.uploadFileType === FILE_TYPES.FOLDER
            ? 'Rename folder'
            : 'Rename file'
        }
        onClose={onClose}
      />
      <ModalContent onSubmit={createItem}>
        <Input
          placeHolder={modalData?.placeholder}
          value={docName}
          onChange={(e) => setDocName(e.target.value)}
          autoFocus
        />
        <ModalButton>
          <Button
            buttonType={'submit'}
            title={isEmpty(itemData?.id) ? modalData?.title : 'Rename'}
            variant={BUTTON_TYPE.PRIMARY}
            isLoading={loading}
          />
        </ModalButton>
      </ModalContent>
    </>
  );
};

export default FilesModal;
